import { useRef } from "react";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
  Divider,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import DoneIcon from "@mui/icons-material/Done";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const BudgetSnapshotSelector = ({ initialSnapshots }) => {
  const snapshotRefs = useRef({});

  // Earliest snapshot that is actually used (baseline)
  const earliestSnapshot =
    initialSnapshots
      .filter(s => s.selected || s.committed) // Only consider snapshots that are used
      .reduce((earliest, s) => {
        return !earliest || new Date(s.created_at) < new Date(earliest.created_at)
          ? s
          : earliest;
      }, null) || {};

  // The month a snapshot answers for is decided server-side (`year_month`) and is not
  // always the month it was captured in -- a CSR taken late is moved to the month it
  // reports on. Deriving the heading from created_at here would file it under the
  // capture month while the calculations use the other one.
  const monthNameOf = (yearMonth) =>
    MONTH_NAMES[Number(String(yearMonth).slice(5, 7)) - 1];

  const capturedMonthLabel = (snapshot) =>
    new Date(snapshot.created_at).toLocaleString("default", {
      month: "long",
      year: "numeric",
    });

  // Group snapshots by Year -> Month
  const groupSnapshotsByYearAndMonth = (snapshots) =>
    snapshots.reduce((acc, snapshot) => {
      const d = new Date(snapshot.created_at);
      const yearMonth =
        snapshot.year_month ||
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const year = Number(String(yearMonth).slice(0, 4));
      acc[year] ||= {};
      acc[year][yearMonth] ||= [];
      acc[year][yearMonth].push(snapshot);
      return acc;
    }, {});

  const snapshotsByYearAndMonth = groupSnapshotsByYearAndMonth(initialSnapshots);

  if (!initialSnapshots?.length) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography align="center">No budget snapshots available.</Typography>
      </Box>
    );
  }

  // Sort years desc (newest first)
  const years = Object.keys(snapshotsByYearAndMonth)
    .map(Number)
    .sort((a, b) => b - a);

  return (
    <Box sx={{ width: "100%" }}>
      {/* Responsive 2-column layout: sticky left rail + scrollable content */}
      <Box
        sx={{
          maxWidth: 1400,
          mx: "auto",
          display: "grid",
          gap: 4,
          gridTemplateColumns: { xs: "1fr", md: "220px 1fr" },
          alignItems: "start",
        }}
      >
        {/* LEFT: Years rail */}
        <Box
          sx={{
            position: { md: "sticky" },
            top: { md: 0 },
            alignSelf: "start",
            bgcolor: "background.paper",
            borderRadius: 1,
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            Years
          </Typography>
          <List dense sx={{ mt: -0.5 }}>
            {years.map((year) => (
              <ListItemButton
                key={year}
                sx={{ py: 0.25 }}
                onClick={() =>
                  snapshotRefs.current[year]?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  })
                }
              >
                <ListItemText
                  primary={year}
                  primaryTypographyProps={{ variant: "body1" }}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>

        {/* RIGHT: Year -> Month -> Snapshots */}
        <Box>
          {years.map((year) => (
            <Box key={year} ref={(el) => (snapshotRefs.current[year] = el)}>
              <Typography variant="h5" sx={{ mb: 1 }}>
                {year}
              </Typography>

              {Object.keys(snapshotsByYearAndMonth[year]).sort().map((yearMonth) => {
                const monthSnapshots = snapshotsByYearAndMonth[year][yearMonth];

                return (
                  <Box key={yearMonth} sx={{ pl: 2, mb: 1.5 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 0.5, mt: 1 }}
                    >
                      {monthNameOf(yearMonth)}
                    </Typography>

                    {monthSnapshots.map((snapshot, idx) => (
                      <Box
                        key={snapshot.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          py: 0.5,
                        }}
                      >
                        <Typography variant="body2">
                          {snapshot.name} (Captured on{" "}
                          {new Date(snapshot.created_at).toLocaleDateString(
                            "en-US"
                          )}
                          )
                        </Typography>

                        <Box
                          className="badge titillium-web-base project-header-title-text-small contract-structure-gmp"
                          sx={{
                            px: 1,
                            py: 0.25,
                            borderRadius: "4px",
                            color: "white",
                            bgcolor:
                              snapshot.snapshot_type === "project_status_snapshot"
                                ? "success.main"
                                : "secondary.main",
                            fontSize: "0.75rem",
                          }}
                        >
                          {snapshot.snapshot_type === "project_status_snapshot"
                            ? "Project Status"
                            : "Ad Hoc"}
                        </Box>

                        {snapshot.overridden && (
                          <Tooltip
                            title={`Captured in ${capturedMonthLabel(snapshot)} and moved here by an administrator, so this month reports on the right snapshot.`}
                          >
                            <Box
                              className="badge titillium-web-base project-header-title-text-small"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                px: 1,
                                py: 0.25,
                                borderRadius: "4px",
                                color: "white",
                                bgcolor: "warning.main",
                                fontSize: "0.75rem",
                              }}
                            >
                              <SwapHorizIcon sx={{ fontSize: 14 }} />
                              Moved from {capturedMonthLabel(snapshot)}
                            </Box>
                          </Tooltip>
                        )}

                        {snapshot.id === earliestSnapshot.id && (
                          <Tooltip title="Baseline Snapshot">
                            <StarIcon sx={{ color: "gold", fontSize: 18 }} />
                          </Tooltip>
                        )}

                        <Box sx={{ ml: "auto" }}>
                          {snapshot.committed ? (
                            <Tooltip title="This Snapshot was reported on the Monthly Cost Status Submission.">
                              <DoneAllIcon
                                sx={{ color: "success.main", fontSize: 18 }}
                              />
                            </Tooltip>
                          ) : snapshot.selected ? (
                            <Tooltip
                              title={
                                snapshot.overridden
                                  ? "This snapshot was assigned to this month by an administrator and is being used for calculations."
                                  : "This snapshot is the latest snapshot taken for the designated month and is being used for calculations."
                              }
                            >
                              <DoneIcon
                                sx={{ color: "success.main", fontSize: 18 }}
                              />
                            </Tooltip>
                          ) : (
                            <Tooltip title="This snapshot is not being used.">
                              <DoneIcon
                                sx={{ color: "text.disabled", fontSize: 18 }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    ))}

                    {/* Divider between months */}
                    <Divider sx={{ my: 1 }} />
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default BudgetSnapshotSelector;
